import { h, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { BirdsnestRangeSlider } from './BirdsnestRangeSlider';
import { BirdsnestColorSwatchGrid } from './BirdsnestColorSwatchGrid';
import { BirdsnestCheckboxList } from './BirdsnestCheckboxList';
import { getAllFiltersFromOrderedItems, resetOrderedFilterItems, syncCountsFromApi, getVisibleOptions, filterHasVisibleContent, } from './BirdsnestFilterDrawer';
import styles from './MobileFilterDrawer.module.scss';
function GroupedFilterSection({ groupId, groupLabel, filters, isOpen, onToggle, openSections, toggleSection, renderFilterContent, }) {
    return (h("div", { class: styles.filterSection, id: `grouped-filter-${groupId}` },
        h("button", { class: styles.filterHeader, onClick: onToggle, type: "button" },
            h("span", { class: styles.filterLabel }, groupLabel.toUpperCase()),
            h("span", { class: styles.filterIcon }, isOpen ? (h("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none" },
                h("path", { d: "M1 7h12", stroke: "currentColor", strokeWidth: "1.5" }))) : (h("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none" },
                h("path", { d: "M7 1v12M1 7h12", stroke: "currentColor", strokeWidth: "1.5" }))))),
        isOpen && (h("div", { class: styles.filterContent }, filters.map(filter => {
            var _a;
            return (h(CollapsibleSection, { key: filter.id, title: filter.label.toUpperCase(), isOpen: (_a = openSections[filter.id]) !== null && _a !== void 0 ? _a : false, onToggle: () => toggleSection(filter.id), isChildFilter: true }, renderFilterContent(filter)));
        })))));
}
function CollapsibleSection({ title, isOpen, onToggle, children, isChildFilter = false }) {
    return (h("div", { class: styles.filterSection },
        h("button", { class: `${styles.filterHeader} ${isChildFilter ? styles.childFilterSection : ''}`, onClick: onToggle, type: "button" },
            h("span", { class: styles.filterLabel }, title),
            h("span", { class: styles.filterIcon }, isOpen ? (h("svg", { width: "14", height: "2", viewBox: "0 0 14 2", fill: "none" },
                h("path", { d: "M1 1h12", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }))) : (h("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none" },
                h("path", { d: "M7 1v12M1 7h12", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }))))),
        isOpen && (h("div", { class: `${styles.filterContent} ${(title === null || title === void 0 ? void 0 : title.toLowerCase().includes('colour')) ? styles.colorFilterContent : ''}` }, children))));
}
export function MobileFilterDrawer({ orderedFilterItems, sortOptions = [], currentSort, onSortChange, activeFilters, onApply, onClearAll, isOpen, onClose, runSearch, query = '', pageSize = 12 }) {
    var _a;
    /** Single local state for the ordered list (replaces localFilters + localGroupedFilters). */
    const [localOrderedFilterItems, setLocalOrderedFilterItems] = useState([]);
    const [localSort, setLocalSort] = useState(currentSort || '');
    const [openSections, setOpenSections] = useState({});
    const [hasInitialized, setHasInitialized] = useState(false);
    // Helper function to merge activeFilters into orderedFilterItems
    const mergeActiveFiltersIntoItems = (items, filters) => {
        return items.map(item => {
            if (item.type === 'filter') {
                const filter = item.filter;
                const activeValues = filters[filter.id];
                if (!activeValues || activeValues.length === 0) {
                    return item;
                }
                // Handle range/slider filters
                if (filter.type === 'range' || filter.type === 'slider') {
                    const rangeValue = activeValues[0];
                    if (rangeValue && rangeValue.includes('-')) {
                        const [minStr, maxStr] = rangeValue.split('-');
                        const currentMin = parseInt(minStr, 10);
                        const currentMax = parseInt(maxStr, 10);
                        return {
                            type: 'filter',
                            filter: Object.assign(Object.assign({}, filter), { currentMin: isNaN(currentMin) ? filter.currentMin : currentMin, currentMax: isNaN(currentMax) ? filter.currentMax : currentMax })
                        };
                    }
                    return item;
                }
                // Handle option-based filters (checkbox, color, etc.)
                if (filter.options) {
                    const updatedOptions = filter.options.map(option => (Object.assign(Object.assign({}, option), { selected: activeValues.includes(option.id) })));
                    return {
                        type: 'filter',
                        filter: Object.assign(Object.assign({}, filter), { options: updatedOptions })
                    };
                }
                return item;
            }
            else {
                // Handle grouped filters
                const updatedFilters = item.group.filters.map(filter => {
                    const activeValues = filters[filter.id];
                    if (!activeValues || activeValues.length === 0) {
                        return filter;
                    }
                    // Handle range/slider
                    if (filter.type === 'range' || filter.type === 'slider') {
                        const rangeValue = activeValues[0];
                        if (rangeValue && rangeValue.includes('-')) {
                            const [minStr, maxStr] = rangeValue.split('-');
                            const currentMin = parseInt(minStr, 10);
                            const currentMax = parseInt(maxStr, 10);
                            return Object.assign(Object.assign({}, filter), { currentMin: isNaN(currentMin) ? filter.currentMin : currentMin, currentMax: isNaN(currentMax) ? filter.currentMax : currentMax });
                        }
                        return filter;
                    }
                    // Handle options
                    if (filter.options) {
                        const updatedOptions = filter.options.map(option => (Object.assign(Object.assign({}, option), { selected: activeValues.includes(option.id) })));
                        return Object.assign(Object.assign({}, filter), { options: updatedOptions });
                    }
                    return filter;
                });
                return {
                    type: 'group',
                    group: Object.assign(Object.assign({}, item.group), { filters: updatedFilters })
                };
            }
        });
    };
    useEffect(() => {
        if (!isOpen) {
            setHasInitialized(false);
            return;
        }
        if (!hasInitialized) {
            const itemsWithActiveFilters = mergeActiveFiltersIntoItems(orderedFilterItems, activeFilters);
            setLocalOrderedFilterItems(itemsWithActiveFilters);
            setLocalSort(currentSort || '');
            setHasInitialized(true);
        }
    }, [isOpen, orderedFilterItems, currentSort, hasInitialized, activeFilters]);
    useEffect(() => {
        const drawerIsOpenAndReady = hasInitialized && isOpen && orderedFilterItems.length > 0;
        if (drawerIsOpenAndReady) {
            setLocalOrderedFilterItems(currentLocalItems => syncCountsFromApi(currentLocalItems, orderedFilterItems));
        }
    }, [orderedFilterItems]);
    const applyFilterChange = (filter, filterId, value) => {
        if (filter.id !== filterId)
            return filter;
        if (filter.type === 'range' || filter.type === 'slider') {
            const [minStr, maxStr] = value.split('-');
            const currentMin = parseInt(minStr, 10);
            const currentMax = parseInt(maxStr, 10);
            return Object.assign(Object.assign({}, filter), { currentMin: isNaN(currentMin) ? filter.currentMin : currentMin, currentMax: isNaN(currentMax) ? filter.currentMax : currentMax });
        }
        const multiSelect = filter.multiSelect !== false;
        const options = (filter.options || []).map(option => {
            if (option.id !== value) {
                return multiSelect ? option : Object.assign(Object.assign({}, option), { selected: false });
            }
            if (multiSelect) {
                return Object.assign(Object.assign({}, option), { selected: !option.selected });
            }
            return Object.assign(Object.assign({}, option), { selected: true });
        });
        return Object.assign(Object.assign({}, filter), { options });
    };
    const handleLocalFilterChange = (filterId, value) => {
        const updatedItems = localOrderedFilterItems.map(item => {
            if (item.type === 'filter') {
                return { type: 'filter', filter: applyFilterChange(item.filter, filterId, value) };
            }
            return {
                type: 'group',
                group: Object.assign(Object.assign({}, item.group), { filters: item.group.filters.map(f => applyFilterChange(f, filterId, value)) }),
            };
        });
        setLocalOrderedFilterItems(updatedItems);
        // Auto-apply filters on change
        const allFilters = getAllFiltersFromOrderedItems(updatedItems);
        const newFiltersFromDrawer = buildFilterMap(allFilters);
        const allFilterIdsInDrawer = new Set(allFilters.map(f => f.id));
        const preservedFilters = {};
        Object.entries(activeFilters).forEach(([filterId, values]) => {
            if (!allFilterIdsInDrawer.has(filterId)) {
                preservedFilters[filterId] = values;
            }
        });
        onApply(Object.assign(Object.assign({}, preservedFilters), newFiltersFromDrawer));
    };
    const getSelectedTags = () => {
        const tags = [];
        const allFilters = getAllFiltersFromOrderedItems(localOrderedFilterItems);
        allFilters.forEach(filter => {
            if (filter.type === 'range' || filter.type === 'slider') {
                const currentMin = filter.currentMin;
                const currentMax = filter.currentMax;
                const baseMin = filter.min;
                const baseMax = filter.max;
                if (typeof currentMin === 'number' &&
                    typeof currentMax === 'number' &&
                    (baseMin === undefined || baseMax === undefined || currentMin !== baseMin || currentMax !== baseMax)) {
                    tags.push({
                        filterId: filter.id,
                        filterLabel: filter.label,
                        optionId: `${currentMin}-${currentMax}`,
                        optionLabel: `${filter.unit || '$'}${currentMin} - ${filter.unit || '$'}${currentMax}`,
                    });
                }
            }
            else if (filter.options) {
                filter.options.forEach(option => {
                    if (option.selected) {
                        tags.push({
                            filterId: filter.id,
                            filterLabel: filter.label,
                            optionId: option.id,
                            optionLabel: option.label,
                        });
                    }
                });
            }
        });
        return tags;
    };
    const selectedTags = getSelectedTags();
    const handleRemoveTag = (filterId, optionId) => {
        const apply = (filter) => {
            var _a, _b;
            if (filter.id !== filterId)
                return filter;
            if (filter.type === 'range' || filter.type === 'slider') {
                return Object.assign(Object.assign({}, filter), { currentMin: (_a = filter.min) !== null && _a !== void 0 ? _a : filter.currentMin, currentMax: (_b = filter.max) !== null && _b !== void 0 ? _b : filter.currentMax });
            }
            return Object.assign(Object.assign({}, filter), { options: (filter.options || []).map(option => option.id === optionId ? Object.assign(Object.assign({}, option), { selected: false }) : option) });
        };
        setLocalOrderedFilterItems(prev => prev.map(item => item.type === 'filter'
            ? { type: 'filter', filter: apply(item.filter) }
            : { type: 'group', group: Object.assign(Object.assign({}, item.group), { filters: item.group.filters.map(apply) }) }));
    };
    const handleClearAllTags = () => {
        setLocalOrderedFilterItems(prev => resetOrderedFilterItems(prev));
        setLocalSort('');
    };
    const toggleSection = (sectionId) => {
        setOpenSections(prev => (Object.assign(Object.assign({}, prev), { [sectionId]: !prev[sectionId] })));
    };
    const buildFilterMap = (filtersToMap) => {
        const result = {};
        filtersToMap.forEach(filter => {
            if (filter.type === 'range' || filter.type === 'slider') {
                const baseMin = filter.min;
                const baseMax = filter.max;
                const currentMin = filter.currentMin;
                const currentMax = filter.currentMax;
                if (typeof currentMin === 'number' &&
                    typeof currentMax === 'number' &&
                    (baseMin === undefined ||
                        baseMax === undefined ||
                        currentMin !== baseMin ||
                        currentMax !== baseMax)) {
                    result[filter.id] = [`${currentMin}-${currentMax}`];
                }
            }
            else if (filter.options && filter.options.length > 0) {
                const selectedValues = filter.options
                    .filter(option => option.selected)
                    .map(option => option.id);
                if (selectedValues.length > 0) {
                    result[filter.id] = selectedValues;
                }
            }
        });
        return result;
    };
    const handleApply = () => {
        const allFilters = getAllFiltersFromOrderedItems(localOrderedFilterItems);
        const newFiltersFromDrawer = buildFilterMap(allFilters);
        const allFilterIdsInDrawer = new Set(allFilters.map(f => f.id));
        const preservedFilters = {};
        Object.entries(activeFilters).forEach(([filterId, values]) => {
            if (!allFilterIdsInDrawer.has(filterId)) {
                preservedFilters[filterId] = values;
            }
        });
        onApply(Object.assign(Object.assign({}, preservedFilters), newFiltersFromDrawer), localSort || 'relevance');
        onClose();
    };
    /** Render from local state when available, otherwise prop (e.g. before first open). */
    const displayItems = localOrderedFilterItems.length ? localOrderedFilterItems : orderedFilterItems;
    const renderFilterContent = (filter) => {
        // Only show options that have products or are currently selected
        const visibleOptions = getVisibleOptions(filter.options || []);
        switch (filter.type) {
            case 'range':
            case 'slider':
                return (h(BirdsnestRangeSlider, { min: filter.min || 0, max: filter.max || 1000, gap: filter.gap || 1, currentMin: filter.currentMin || filter.min || 0, currentMax: filter.currentMax || filter.max || 1000, unit: filter.unit || '$', onChange: (values) => {
                        handleLocalFilterChange(filter.id, `${values[0]}-${values[1]}`);
                    } }));
            case 'color':
            case 'swatch':
                return (h(BirdsnestColorSwatchGrid, { options: visibleOptions, onChange: (optionId) => handleLocalFilterChange(filter.id, optionId) }));
            case 'checkbox':
            case 'text':
            case 'radio':
            default:
                return (h(BirdsnestCheckboxList, { options: visibleOptions, onChange: (optionId) => handleLocalFilterChange(filter.id, optionId), multiSelect: filter.multiSelect !== false, isColor: Boolean(filter.id === "custom_colour_group") }));
        }
    };
    return (h(Fragment, null,
        isOpen && h("div", { class: styles.overlay, onClick: onClose }),
        h("div", { class: `${styles.drawer} ${isOpen ? styles.drawerOpen : ''}` },
            h("div", { class: styles.drawerHeader },
                h("h2", { class: styles.drawerTitle }, "Filter"),
                h("button", { class: styles.closeBtn, onClick: onClose, type: "button", "aria-label": "Close filters" },
                    h("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none" },
                        h("path", { d: "M2 2l16 16M2 18L18 2", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" })))),
            selectedTags.length > 0 && (h("div", { class: styles.selectedTagsSection },
                h("div", { class: styles.selectedTags }, selectedTags.map(tag => (h("button", { key: `${tag.filterId}-${tag.optionId}`, class: styles.selectedTag, onClick: () => handleRemoveTag(tag.filterId, tag.optionId), type: "button" },
                    h("span", { class: styles.tagLabel }, tag.optionLabel.toUpperCase()),
                    h("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", class: styles.tagClose },
                        h("path", { d: "M2 2l8 8M2 10l8-8", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" })))))),
                h("button", { class: styles.clearAllLink, onClick: handleClearAllTags, type: "button" }, "CLEAR ALL"))),
            h("div", { class: styles.divider }),
            h("div", { class: styles.filterSections },
                sortOptions.length > 0 && (h(CollapsibleSection, { title: "SORT BY", isOpen: (_a = openSections['sort']) !== null && _a !== void 0 ? _a : false, onToggle: () => toggleSection('sort') },
                    h("div", { class: styles.radioList }, sortOptions.map(option => (h("label", { key: option.id, class: `${styles.radioItem} ${localSort === option.id ? styles.selected : ''}` },
                        h("span", { class: styles.radioWrapper },
                            h("input", { type: "radio", name: "sort", checked: localSort === option.id, onChange: () => setLocalSort(option.id), class: styles.radioInput }),
                            h("span", { class: `${styles.radioCustom} ${localSort === option.id ? styles.checked : ''}` }, localSort === option.id && h("span", { class: styles.radioDot }))),
                        h("span", { class: styles.radioLabel }, option.label))))))),
                displayItems.map((item) => {
                    var _a, _b;
                    if (item.type === 'filter' && !filterHasVisibleContent(item.filter)) {
                        return null;
                    }
                    if (item.type === 'group' && !item.group.filters.some(filterHasVisibleContent)) {
                        return null;
                    }
                    return item.type === 'filter' ? (h(CollapsibleSection, { key: item.filter.id, title: item.filter.label.toUpperCase(), isOpen: (_a = openSections[item.filter.id]) !== null && _a !== void 0 ? _a : false, onToggle: () => toggleSection(item.filter.id) }, renderFilterContent(item.filter))) : (h(GroupedFilterSection, { key: item.group.groupId, groupId: item.group.groupId, groupLabel: item.group.groupLabel, filters: item.group.filters.filter(filterHasVisibleContent), isOpen: (_b = openSections[item.group.groupId]) !== null && _b !== void 0 ? _b : false, onToggle: () => toggleSection(item.group.groupId), openSections: openSections, toggleSection: toggleSection, renderFilterContent: renderFilterContent }));
                })),
            h("div", { class: styles.drawerFooter },
                h("button", { class: styles.clearBtn, onClick: () => {
                        setLocalOrderedFilterItems(prev => resetOrderedFilterItems(prev));
                        setLocalSort('');
                        onClearAll();
                    }, type: "button" }, "CLEAR ALL"),
                h("button", { class: styles.applyBtn, onClick: handleApply, type: "button" }, "APPLY")))));
}
